import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Clock, CalendarCheck, ArrowRight } from 'lucide-react';
import SEO, { generateBreadcrumbSchema, generateArticleSchema } from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { getGuide, guides } from '../data/guides';
import { countyLocations } from '../data/locations';

const GuideDetail = () => {
  const { slug } = useParams();
  const guide = getGuide(slug);
  if (!guide) return <Navigate to="/guides" replace />;

  const path = `/guides/${guide.slug}`;
  const related = guides.filter((g) => g.slug !== guide.slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-white" data-testid={`guide-page-${guide.slug}`}>
      <SEO title={guide.title} rawTitle description={guide.metaDescription} url={path} image={guide.image} type="article"
        structuredData={[generateArticleSchema(guide, path), generateBreadcrumbSchema([{ name: 'Guides', path: '/guides' }, { name: guide.title, path }])]} />

      <div className="relative h-[420px]">
        <img src={guide.image} alt={guide.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-slate-900/20" />
        <div className="relative max-w-4xl mx-auto px-4 h-full flex flex-col justify-end pb-10 text-white">
          <div className="mb-4"><Breadcrumbs light items={[{ name: 'Guides', path: '/guides' }, { name: guide.category }]} /></div>
          <div className="flex items-center gap-3 text-sm text-slate-200 mb-3"><Badge className="bg-orange-500 hover:bg-orange-600">{guide.category}</Badge><span className="flex items-center gap-1"><Clock className="h-4 w-4" />{guide.readTime}</span></div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">{guide.title}</h1>
        </div>
      </div>

      <article className="max-w-4xl mx-auto px-4 py-12">
        <p className="text-lg md:text-xl text-slate-600 mb-10 leading-relaxed">{guide.excerpt}</p>
        {guide.sections.map((s) => (
          <section key={s.h} className="mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">{s.h}</h2>
            {s.p.map((para, i) => <p key={i} className="text-slate-700 text-base md:text-lg leading-relaxed mb-4">{para}</p>)}
          </section>
        ))}

        <div className="bg-slate-900 text-white rounded-2xl p-8 my-12" data-testid="guide-cta">
          <h2 className="text-2xl font-bold mb-2">Ready to plan your own trip?</h2>
          <p className="text-slate-300 mb-6">Check live prices and availability for our Suffolk-based fleet and book online in minutes. Insurance, unlimited mileage and free parking included.</p>
          <div className="flex flex-wrap gap-3">
            <Button asChild size="lg" className="bg-green-600 hover:bg-green-700 font-bold"><Link to="/#book-online"><CalendarCheck className="mr-2 h-5 w-5" /> Check Prices & Book</Link></Button>
            <Button asChild size="lg" variant="outline" className="bg-white/10 text-white border-white hover:bg-white hover:text-slate-900"><Link to="/motorhomes">View the fleet</Link></Button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-10 border-t pt-10">
          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-4">More guides</h2>
            <ul className="space-y-3">{related.map((g) => <li key={g.slug}><Link to={`/guides/${g.slug}`} className="text-blue-700 hover:underline flex items-start gap-2"><ArrowRight className="h-4 w-4 mt-1 flex-shrink-0" />{g.title}</Link></li>)}</ul>
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-4">Motorhome hire near you</h2>
            <ul className="space-y-3">{countyLocations.map((l) => <li key={l.slug}><Link to={`/motorhome-hire/${l.slug}`} className="text-blue-700 hover:underline flex items-start gap-2"><ArrowRight className="h-4 w-4 mt-1 flex-shrink-0" />Motorhome hire {l.name}</Link></li>)}</ul>
          </div>
        </div>
      </article>
    </div>
  );
};

export default GuideDetail;
